import {createListCollection, HStack, IconButton, SelectValueChangeDetails, StackProps} from "@chakra-ui/react";
import {typedEntries, typedMemo} from "../../../../utils/typescript";
import {RefObject, SetStateAction, useCallback, useMemo} from "react";
import {SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText} from "../../../ui/select";
import {BarsArrowDownIcon, BarsArrowUpIcon} from "@heroicons/react/16/solid";
import {Tooltip} from "../../../ui/tooltip";
import {
  ExtractTableContext,
  isColumnHidden,
  isColumnSortable,
  resolveSortOpts,
  SortState,
  TableConfig,
} from "../../../../hooks/table";
import useTranslate from "platformed-components/i18n/useTranslate";
import T from "platformed-components/i18n/T";

export const OrderingSelector = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    sortState,
    onSortStateChange,
    portalRef,
    containerProps,
  }: {
    config: C;
    context: ExtractTableContext<C>;
    sortState: SortState<C>;
    onSortStateChange: (action: SetStateAction<SortState<C>>) => void;
    portalRef: RefObject<HTMLDivElement>;
    containerProps?: StackProps;
  }) => {
    const t = useTranslate();
    const sortableColumns = useMemo(
      () => typedEntries(config.columns).filter(([_, column]) => isColumnSortable(column)),
      [config.columns],
    );

    const collection = useMemo(
      () =>
        createListCollection({
          items: sortableColumns.map(([columnId, column]) => ({
            label: column.overrideGroupAndSortLabel ?? column.label,
            labelNode: column.label,
            value: columnId,
            hidden: isColumnHidden(column, context),
          })),
        }),
      [context, sortableColumns],
    );

    const onSelectValueChange = useCallback(
      (d: SelectValueChangeDetails) => {
        const columnId = d.value[0];
        const sortOpts = resolveSortOpts(config.columns[columnId]);
        if (sortOpts == null) return;

        const direction = sortOpts.defaultDirection;
        onSortStateChange({columnId, direction});
      },
      [config.columns, onSortStateChange],
    );

    const onClickDirectionButton = useCallback(() => {
      onSortStateChange(s => {
        if (s == null) {
          return null;
        }

        return {...s, direction: s.direction === "ascending" ? "descending" : "ascending"};
      });
    }, [onSortStateChange]);

    return (
      <HStack {...containerProps}>
        <SelectRoot
          collection={collection}
          size="sm"
          value={[(sortState?.columnId as string | undefined) ?? "None"]}
          onValueChange={onSelectValueChange}
        >
          <SelectTrigger>
            <SelectValueText placeholder={t("c.None")} />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {collection.items
              .filter(item => !item.hidden)
              .map(item => (
                <SelectItem item={item} key={item.value}>
                  {item.labelNode ?? item.label}
                </SelectItem>
              ))}
          </SelectContent>
        </SelectRoot>
        {sortState != null && (
          <Tooltip
            content={sortState.direction === "ascending" ? <T keyName="c.Ascending" /> : <T keyName="c.Descending" />}
          >
            <IconButton size="sm" variant="outline" onClick={onClickDirectionButton}>
              {sortState.direction === "ascending" ? <BarsArrowDownIcon /> : <BarsArrowUpIcon />}
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    );
  },
);
